import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useUser } from '../contexts/UserContext';
import Navigation from '../components/Navigation';
import MoodSelector, { getMoodInfo } from '../components/MoodSelector';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Diary = () => {
  const { getAgeConfig, fetchStats } = useUser();
  const config = getAgeConfig();
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({ content: '', mood: 3 });

  const fetchEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/diary`, { withCredentials: true });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching diary:', error);
      toast.error('Error al cargar el diario');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const createEntry = async () => {
    if (!newEntry.content.trim()) {
      toast.error('Escribe algo en tu diario');
      return;
    }

    try {
      const response = await axios.post(`${API}/diary`, newEntry, { withCredentials: true });
      setEntries([response.data, ...entries]);
      setNewEntry({ content: '', mood: 3 });
      setIsDialogOpen(false);
      fetchStats();
      toast.success('¡Entrada guardada! +3 de energía');
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Error al guardar entrada');
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/diary/${entryId}`, { withCredentials: true });
      setEntries(entries.filter(e => e.entry_id !== entryId));
      toast.success('Entrada eliminada');
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Error al eliminar entrada');
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, "HH:mm", { locale: es });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8 lg:pl-20">
      <Navigation />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className={`${config.fontClass} text-2xl sm:text-3xl font-bold text-foreground`}>
              Mi Diario
            </h1>
            <p className="text-muted-foreground">
              {entries.length} {entries.length === 1 ? 'entrada' : 'entradas'}
            </p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#4F8CFF] hover:bg-[#3A75E6] text-white rounded-full"
                data-testid="add-diary-btn"
              >
                <Plus className="w-5 h-5 mr-1" />
                Nueva
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>¿Cómo te sientes hoy?</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-4">
                <MoodSelector 
                  value={newEntry.mood} 
                  onChange={(mood) => setNewEntry({ ...newEntry, mood })} 
                />
                
                <Textarea
                  placeholder="Escribe lo que sientes..."
                  value={newEntry.content}
                  onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                  className="min-h-[150px] resize-none"
                  data-testid="diary-content-input"
                />
                
                <Button 
                  onClick={createEntry}
                  className="w-full bg-[#4F8CFF] hover:bg-[#3A75E6] text-white"
                  data-testid="save-diary-btn"
                >
                  Guardar Entrada
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Entries list */}
        <div className="space-y-4">
          <AnimatePresence>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <p className="text-muted-foreground mb-4">
                  Tu diario está vacío. ¡Escribe tu primera entrada!
                </p>
                <Button
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-[#4F8CFF] hover:bg-[#3A75E6] text-white rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Escribir primera entrada
                </Button>
              </motion.div>
            ) : (
              entries.map((entry, index) => {
                const moodInfo = getMoodInfo(entry.mood);
                
                return (
                  <motion.div
                    key={entry.entry_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    layout
                  >
                    <Card className={`glass ${config.cardRadius} overflow-hidden`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                              style={{ backgroundColor: `${moodInfo.color}20` }}
                            >
                              {moodInfo.emoji}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{moodInfo.label}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Calendar className="w-3 h-3" />
                                <span>{formatDate(entry.created_at)}</span>
                                <span>•</span>
                                <span>{formatTime(entry.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => deleteEntry(entry.entry_id)}
                            className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                            data-testid={`diary-delete-${entry.entry_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <p className="text-foreground whitespace-pre-wrap">
                          {entry.content}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default Diary;
